import { useEffect, useState } from 'react';
import { TrendingUp, CheckCircle2, Package, MapPin } from 'lucide-react';
import { apiUrl } from '../../utils/api';

interface PlatformStats {
  activeCampaigns: number;
  completedCampaigns: number;
  productsDelivered: number;
  citiesReached: number;
}

export function ImpactStats() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      try {
        const res = await fetch(apiUrl('/api/stats/public'));
        const data = await res.json();
        if (data.success && data.data) {
          setStats(data.data);
        }
      } catch {
        // Silently fail
      } finally {
        setIsLoading(false);
      }
    }
    fetchStats();
  }, []);

  if (isLoading) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="text-center animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-16 mx-auto mb-2" />
              <div className="h-3 bg-gray-100 rounded w-24 mx-auto" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!stats) return null;

  const totalActivity = stats.activeCampaigns + stats.completedCampaigns + stats.productsDelivered + stats.citiesReached;
  const isLowActivity = totalActivity < 10;

  const statItems = [
    { icon: TrendingUp, value: stats.activeCampaigns, label: 'Campañas activas', color: 'text-indigo-600' },
    { icon: CheckCircle2, value: stats.completedCampaigns, label: 'Campañas completadas', color: 'text-green-600' },
    { icon: Package, value: stats.productsDelivered, label: 'Productos entregados', color: 'text-amber-600' },
    { icon: MapPin, value: stats.citiesReached, label: 'Ciudades alcanzadas', color: 'text-rose-600' },
  ];

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-8">
        {isLowActivity && (
          <p className="text-center text-sm text-gray-500 mb-6 font-medium">
            🌱 Plataforma en crecimiento — cada aporte suma
          </p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {statItems.map(({ icon: Icon, value, label, color }) => (
            <div key={label} className="text-center">
              <Icon className={`w-6 h-6 mx-auto mb-2 ${color}`} />
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                {value > 0 ? value.toLocaleString() : '—'}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
